import {Component, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {Subscription} from "rxjs";
import {AuthGuard} from "src/app/common/auth-guard.service";
import {Alarm} from "src/app/model/alarm";
import {AlarmFilters} from "src/app/model/filters/alarm-filters";
import {Machine} from "src/app/model/machine";
import {SortOrder} from "src/app/model/sort-order";
import {AlarmsService} from "src/app/services/alarms.service";
import {CommonMessagesService} from "src/app/services/common-messages.service";
import {MachineDetailsService} from "src/app/services/machine-details.service";
import {MachineGroupsService} from "src/app/services/machine-groups.service";
import {GenericGraphComponent} from "../../../shared/generic-graph/generic-graph.component";
import {MenuItem} from "primeng/api";
import {MachineAlarmsChartsResponse} from "../../../../model/responses/machine-alarms-charts-response";
import {TimezoneService} from "../../../../services/timezone.service";
import {AlarmType} from "../../../../model/alarm-type";
import {CommonTranslationsService} from "../../../../services/common-translations.service";
import {AppConfig} from "../../../../shared/global-variables";
import {ExportFileType} from "../../../../model/export-file-type";

@Component({
  selector: 'app-machine-details-issues',
  templateUrl: './machine-details-issues.component.html',
  styleUrls: ['./machine-details-issues.component.scss']
})
export class MachineDetailsIssuesComponent implements OnInit, OnDestroy {

  @ViewChild('onVsOffChart')
  public onVsOffChart: GenericGraphComponent;

  @ViewChild('alarmedVsNotAlarmedChart')
  public alarmedVsNotAlarmedChart: GenericGraphComponent;

  @ViewChild('alarmDistributionChart')
  public alarmDistributionChart: GenericGraphComponent;

  public alarms: Alarm[];
  public totalAlarms: number;
  public areAlarmsLoading: boolean = true;
  public areAlarmChartsLoaded: boolean = false;

  public rows: number = 10;
  public first: number = 0;

  public filtersVisible: boolean;
  public currentFilters: AlarmFilters;

  public activationDate: number;
  public deactivationDate: number;

  public onVsOff: [{
    data: { name: string, y: number, color: string, custom: { time: string, total: string } }[]
  }] = [{data: []}]
  public onVsOffCategories: string[];
  public alarmedVsNotAlarmed: [{
    data: { name: string, y: number, color: string, custom: { time: string, total: string } }[]
  }] = [{data: []}]
  public alarmedVsNotAlarmedCategories: string[];
  public alarmDistribution: [{ data: { name: string, y: number, color: string }[] }] = [{data: []}];
  public alarmDistributionCategories: string[] = [];
  public alarmTypeOptions: { label: string, value: AlarmType }[] = [];

  public operationsTranslation: { [key: string]: string } = {};

  public pointFormatPieChart: string;
  public pointFormatPieChartTime: string;

  public availableYearRange: string;

  public exportVisible: boolean;
  public chartActions: MenuItem[];
  public exportFileType: ExportFileType;

  private readonly colors: string[] = [];

  private powerOnDurationTranslation: string;
  private powerOffDurationTranslation: string;
  private alarmedDurationTranslation: string;
  private notAlarmedDurationTranslation: string;
  private daysTranslation: string;
  private hoursTranslation: string;
  private minutesTranslation: string;
  private secondsTranslation: string;

  private hierarchy: any[];
  private organizations: any[];
  private expandedRows: {};

  private exportChart;


  constructor(
    private readonly machineDetailsService: MachineDetailsService,
    private readonly route: ActivatedRoute,
    private readonly alarmsService: AlarmsService,
    private readonly machineGroupsService: MachineGroupsService,
    private readonly i18n: TranslocoService,
    private readonly commonMessagesService: CommonMessagesService,
    private readonly timezoneService: TimezoneService,
    private readonly commonTranslationsService: CommonTranslationsService,
    public authGuardService: AuthGuard
  ) {
    this.currentFilters = new AlarmFilters();
    this.chartActions = [];
    this.colors = AppConfig.COLORS;
  }

  public machine: Machine;
  public isMachineLoading: boolean = true

  private machineDetailsSubscription: Subscription;

  ngOnInit(): void {
    this.alarms = new Array(this.rows);
    this.availableYearRange = this.timezoneService.calcYearRange();

    this.machineDetailsSubscription = this.machineDetailsService.$notifier.subscribe(machineDetails => {
      this.machine = machineDetails.machine;
      this.isMachineLoading = machineDetails.machineLoading;
      this.loadAlarms(this.first / this.rows, this.rows);
    })

    this.i18n.selectTranslation().subscribe(() => {
      this.powerOnDurationTranslation = this.i18n.translate('attribute.powerOnDuration');
      this.powerOffDurationTranslation = this.i18n.translate('attribute.powerOffDuration');
      this.alarmedDurationTranslation = this.i18n.translate('attribute.alarmedDuration');
      this.notAlarmedDurationTranslation = this.i18n.translate('attribute.notAlarmedDuration');
      this.daysTranslation = (this.i18n.translate('time.days:short'));
      this.hoursTranslation = this.i18n.translate('time.hours:short');
      this.minutesTranslation = this.i18n.translate('time.minutes:short');
      this.secondsTranslation = this.i18n.translate('time.seconds:short');

      this.pointFormatPieChart = this.i18n.translate('misc.alarms') + ': {point.y} ({point.percentage:.1f} %)<br>' + this.i18n.translate('attribute.total') + ': {point.total}';
      this.pointFormatPieChartTime = this.i18n.translate('attribute.duration') + ': {point.custom.time} ({point.percentage:.1f} %)<br>' + this.i18n.translate('attribute.total') + ': {point.custom.total}';
      this.buildChartActions();
    });
    this.buildAlarmTypeOptions();
    this.buildOperationsTranslation();
  }

  private loadAlarms(offset: number, limit: number): void {
    if (!this.isMachineLoading) {
      this.areAlarmsLoading = true;
      this.alarms = new Array(this.rows);
      this.trimFieldsForm(this.currentFilters);
      this.alarmsService.getAlarmsByMachineSerial(this.machine.serial, offset, limit, this.currentFilters).subscribe((response) => {
        this.alarms = response.data.content;
        this.totalAlarms = response.data.totalElements;

        if (this.alarms.length > 0 && !this.areAlarmChartsLoaded) {
          this.alarmsService.getAlarmsChartsByMachineUuid(this.machine.machineUuid).subscribe((response: MachineAlarmsChartsResponse) => {
            const total = this.milliSecondsToTime(response.totalDuration);
            this.onVsOff = [{
              data: [
                {
                  name: this.powerOnDurationTranslation,
                  y: response.powerOnDuration,
                  color: getComputedStyle(document.documentElement).getPropertyValue('--primary-color'),
                  custom: {
                    time: this.milliSecondsToTime(response.powerOnDuration),
                    total
                  }
                },
                {
                  name: this.powerOffDurationTranslation,
                  y: response.powerOffDuration,
                  color: getComputedStyle(document.documentElement).getPropertyValue('--custom-gray'),
                  custom: {
                    time: this.milliSecondsToTime(response.powerOffDuration),
                    total
                  }
                }
              ]

            }];
            this.onVsOffCategories = [this.powerOnDurationTranslation, this.powerOffDurationTranslation];

            this.alarmedVsNotAlarmed = [{
              data: [
                {
                  name: this.alarmedDurationTranslation,
                  y: response.alarmedDuration,
                  color: "#964100",
                  custom: {
                    time: this.milliSecondsToTime(response.alarmedDuration),
                    total
                  }
                },
                {
                  name: this.notAlarmedDurationTranslation,
                  y: response.notAlarmedDuration,
                  color: getComputedStyle(document.documentElement).getPropertyValue('--custom-gray'),
                  custom: {
                    time: this.milliSecondsToTime(response.notAlarmedDuration),
                    total
                  }
                }
              ]

            }];
            this.alarmedVsNotAlarmedCategories = [this.alarmedDurationTranslation, this.notAlarmedDurationTranslation];

            this.alarmDistribution = [{
              data: Object.entries(response.alarmDistribution).map((entry, index) => {
                return {name: entry[0], y: entry[1], color: this.colors[index % this.colors.length]}
              })
            }]
            this.alarmDistributionCategories = Object.keys(response.alarmDistribution);
          }).add(() => this.areAlarmChartsLoaded = true)
        } else this.areAlarmChartsLoaded = true;
      }).add(() => this.areAlarmsLoading = false)
    }
  }

  private buildAlarmTypeOptions(): void {
    this.commonTranslationsService.getAlarmTypeOptions().subscribe(alarmTypeOptions => {
      this.alarmTypeOptions = alarmTypeOptions;
    });
  }

  private buildOperationsTranslation(): void {
    this.commonTranslationsService.getOperationsTranslation().subscribe(operationsTranslation => {
      this.operationsTranslation = operationsTranslation;
    });
  }

  private buildChartActions(): void {
    this.chartActions = [
      {label: this.i18n.translate('common.exportXls'), icon: 'pi pi-file-excel', command: () => this.showExportToXls()},
      {label: this.i18n.translate('common.downloadPng'), icon: 'pi pi-download', command: () => this.downloadChart()}
    ];
  }

  deactivateAlarm(alarmUuid: string): void {
    this.commonMessagesService.showRequestConfirmation('info.deactivateAlarm', () => {
      this.alarmsService.deactivateAlarm(alarmUuid).subscribe(() => {
        this.commonMessagesService.showSuccessfullyResetMessage();
        this.loadAlarms(this.first / this.rows, this.rows);
      }, () => {
        this.commonMessagesService.showErrorWhileResettingAlarmMessage();
      });
    })
  }

  trimFieldsForm(filters: AlarmFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] != undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  onPageChange(event): void {
    this.expandedRows = {};
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.route.params.subscribe(() => this.loadAlarms(event.first / event.rows, event.rows));
  }

  resetFilters(): void {
    this.currentFilters = new AlarmFilters();
    this.activationDate = null;
    this.deactivationDate = null;
    this.currentFilters.sortField = 'activationDate';
    this.currentFilters.sortOrder = 1;
    this.expandedRows = {};
    this.organizations = [];
  }

  applyFilters(): void {
    this.first = 0;
    if (this.organizations != null && this.organizations.length > 0) {
      this.currentFilters.organizationUuids = this.organizations.map(organization => organization.data);
    } else {
      delete this.currentFilters['organizationUuids'];
    }

    this.loadAlarms(this.first / this.rows, this.rows);
  }

  setDate(name: string, value: Date): void {
    if (value) this.currentFilters[name] = this.timezoneService.timestampFromCalendarDataWithTime(value);
    else delete this.currentFilters[name];
  }

  public calculateDuration(activationDate: string, deactivationDate: string | null): string {
    if (deactivationDate != null) {
      const timeInMilliSeconds = (new Date(deactivationDate).getTime() - new Date(activationDate).getTime());
      return this.milliSecondsToTime(timeInMilliSeconds);
    } else return '';
  }

  showExportToXls(): void {
    this.exportVisible = true;
  }

  downloadChart(): void {
    try {
      setTimeout(() => {
        if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_ON_VS_OFF_CHART) {
          if (this.onVsOffChart.chart.axes != undefined) {
            this.exportChart = this.onVsOffChart.chart;
          }
          this.onVsOffChart.download(this.exportChart);
        } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_CHART) {
          if (this.alarmedVsNotAlarmedChart.chart.axes != undefined) {
            this.exportChart = this.alarmedVsNotAlarmedChart.chart;
          }
          this.alarmedVsNotAlarmedChart.download(this.exportChart);
        } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_DISTRIBUTION_CHART) {
          if (this.alarmDistributionChart.chart.axes != undefined) {
            this.exportChart = this.alarmDistributionChart.chart;
          }
          this.alarmDistributionChart.download(this.exportChart);
        }
      });
    } catch (ignored) {
    }
  }

  public getContentExport(): any {
    if (this.exportFileType === ExportFileType.MACHINE_ALARMS_LIST) {
      return this.alarms;
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_ON_VS_OFF_CHART) {
      return this.onVsOff[0].data;
    } else if (this.exportFileType === ExportFileType.MACHINE_ALARMS_TIME_CHART) {
      return this.alarmedVsNotAlarmed[0].data;
    } else {
      return this.alarmDistribution[0].data;
    }
  }

  public milliSecondsToTime(milliSeconds: number): string {
    return this.timezoneService.milliSecondsToTime(milliSeconds, this.daysTranslation, this.hoursTranslation,
      this.minutesTranslation, this.secondsTranslation);
  }

  ngOnDestroy(): void {
    if (this.machineDetailsSubscription != null) this.machineDetailsSubscription.unsubscribe();
  }

  public readonly ExportFileType = ExportFileType;
}



